import { Heading, Text, Button, Badge, Card, Callout, TextField, IconButton } from 'frosted-ui';
import { Plus16, XMark16, InfoCircle16 } from '@frosted-ui/icons';
import { SPOTS, linkName, slugify, spotById, type SpotId } from '../../utils/paths';
import { newSpotDraft, type SpotDraft } from './wizardTypes';
import { useCompanyRoute } from '../../contexts/CompanyContext';

interface StepPlaceLinkProps {
  spots: SpotDraft[];
  currentKey: string;
  onChange: (spots: SpotDraft[], currentKey: string) => void;
  /** A new one-off offer has no old link to swap out. */
  isUnlock?: boolean;
}

/** Channel-specific "how to place it" card, also used when adding a spot later. */
export function SpotInstructions({ spotId }: { spotId: SpotId }) {
  const spot = spotById(spotId);
  return (
    <Card size="2">
      <Heading size="3" style={{ marginBottom: 'var(--space-3)' }}>
        {spot.howTitle}
      </Heading>
      <ol style={{ margin: 0, paddingLeft: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {spot.steps.map((step) => (
          <li key={step}>
            <Text size="2">{step}</Text>
          </li>
        ))}
      </ol>
    </Card>
  );
}

export function ReplaceDontAdd() {
  return (
    <Callout.Root color="blue" variant="surface">
      <Callout.Icon>
        <InfoCircle16 />
      </Callout.Icon>
      <Callout.Text>
        <strong>Replace, don't add.</strong> Only buyers who open this link are in the test — your
        store page and old links aren't.
      </Callout.Text>
    </Callout.Root>
  );
}

/** Spot chips for one link: Bio link / Discord pin / … / Somewhere else. */
export function SpotChips({ value, onSelect }: { value: SpotId; onSelect: (id: SpotId) => void }) {
  return (
    <div role="group" aria-label="Where the link goes" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
      {SPOTS.map((s) => (
        <Button
          key={s.id}
          type="button"
          size="2"
          variant={value === s.id ? 'solid' : 'surface'}
          color={value === s.id ? undefined : 'gray'}
          aria-pressed={value === s.id}
          onClick={() => onSelect(s.id)}
        >
          {s.label}
        </Button>
      ))}
    </div>
  );
}

export default function StepPlaceLink({ spots, currentKey, onChange, isUnlock = false }: StepPlaceLinkProps) {
  const current = spots.find((s) => s.key === currentKey) ?? spots[0];
  const spot = spotById(current.spotId);
  const companyRoute = useCompanyRoute();

  function update(key: string, updates: Partial<SpotDraft>) {
    onChange(
      spots.map((s) => (s.key === key ? { ...s, ...updates } : s)),
      currentKey
    );
  }

  function selectSpot(spotId: SpotId) {
    update(current.key, {
      spotId,
      label: spotId === 'other' ? '' : spotById(spotId).label,
    });
  }

  function addSpot() {
    const used = new Set(spots.map((s) => s.spotId));
    const next = SPOTS.find((s) => !used.has(s.id))?.id ?? 'other';
    const draft = newSpotDraft(next);
    onChange([...spots, draft], draft.key);
  }

  function removeSpot(key: string) {
    const remaining = spots.filter((s) => s.key !== key);
    if (remaining.length === 0) return;
    onChange(remaining, key === currentKey ? remaining[0].key : currentKey);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div>
        <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
          Where will this link go?
        </Heading>
        <Text size="2" color="gray">
          Name it so results show where buyers came from. You can add more links, all feeding
          the same test.
        </Text>
      </div>

      {spots.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Text size="2" color="gray">Links in this test:</Text>
          {spots.map((s) => (
            <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              <Button
                type="button"
                size="1"
                variant={s.key === current.key ? 'soft' : 'surface'}
                color={s.key === current.key ? undefined : 'gray'}
                onClick={() => onChange(spots, s.key)}
              >
                {s.label || 'Unnamed link'}
              </Button>
              <IconButton
                type="button"
                size="1"
                variant="ghost"
                color="gray"
                aria-label={`Remove ${s.label || 'link'}`}
                onClick={() => removeSpot(s.key)}
              >
                <XMark16 />
              </IconButton>
            </span>
          ))}
        </div>
      )}

      <SpotChips value={current.spotId} onSelect={selectSpot} />

      {current.spotId === 'other' && (
        <div>
          <Text as="label" size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
            Name this link
          </Text>
          <TextField.Root size="2" variant="surface">
            <TextField.Input
              autoFocus
              placeholder="e.g. Link-in-bio page, Newsletter footer"
              maxLength={60}
              value={current.label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                update(current.key, { label: e.target.value })
              }
            />
          </TextField.Root>
        </div>
      )}

      <Card size="2" style={{ backgroundColor: 'var(--accent-a2)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
          <Text size="2" weight="medium">
            Your {current.label ? linkName(current.label) : 'new link'}
          </Text>
          <Badge size="1" variant="soft" color="gray">
            Works once you go live
          </Badge>
        </div>
        <Text size="2" color="gray" style={{ fontFamily: 'var(--code-font-family, monospace)', wordBreak: 'break-all' }}>
          {current.link ??
            `…/g/${companyRoute ?? 'your-store'}/${current.spotId === 'other' ? slugify(current.label) || spot.code : spot.code}`}
        </Text>
        <Text size="1" color="gray" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
          {current.link
            ? 'Changing its name keeps this link.'
            : "You'll copy your link right after you go live."}
        </Text>
      </Card>

      {isUnlock ? (
        <Text size="2" color="gray">
          Post it wherever you'd sell this. Anyone who opens it lands on your offer page.
        </Text>
      ) : (
        <>
          <SpotInstructions spotId={current.spotId} />
          <ReplaceDontAdd />
        </>
      )}

      <div>
        <Button type="button" variant="ghost" size="2" onClick={addSpot} disabled={spots.length >= 6}>
          <Plus16 />
          Add another link
        </Button>
      </div>
    </div>
  );
}
